<script lang="ts">
  import { onMount } from "svelte"
  import { MoveRight } from "lucide-svelte"
  import { Button } from "$lib/components/ui/button"
  import {
    getRequirement,
    pluralize,
    updateCompetitionEntry,
  } from "$lib/components/Widget2/utils"
  import Instagram from "./ActionDetailTypes/Instagram.svelte"
  import X from "./ActionDetailTypes/X.svelte"
  import Facebook from "./ActionDetailTypes/Facebook.svelte"
  import Referral from "./ActionDetailTypes/Referral.svelte"
  import Tiktok from "./ActionDetailTypes/Tiktok.svelte"
  import Pinterest from "./ActionDetailTypes/Pinterest.svelte"
  import SecretCode from "./ActionDetailTypes/SecretCode.svelte"
  import Telegram from "./ActionDetailTypes/Telegram.svelte"
  import CustomAction from "./ActionDetailTypes/Custom.svelte"
  import Google from "./ActionDetailTypes/Google.svelte"
  import Youtube from "./ActionDetailTypes/Youtube.svelte"
  import Reddit from "./ActionDetailTypes/Reddit.svelte"
  import PromoAmp from "./ActionDetailTypes/PromoAmp.svelte"
  import LinkedIn from "./ActionDetailTypes/LinkedIn.svelte"
  import Threads from "./ActionDetailTypes/Threads.svelte"
  import AppleMusic from "./ActionDetailTypes/AppleMusic.svelte"
  import CouponWheel from "./ActionDetailTypes/CouponWheel.svelte"
  import Spotify from "./ActionDetailTypes/Spotify.svelte"
  import WhatsApp from "./ActionDetailTypes/WhatsApp.svelte"
  import userStore from "$lib/stores/userStore"
  import { userStoreActions } from "$lib/stores/userStore"
  import { statsStore, statsStoreActions } from "$lib/stores/statsStore"
  import { encryptJson } from "$lib/utils/encryption"
  import { PUBLIC_HASH } from "$env/static/public"
  import { t, getCurrentLanguage } from "$lib/utils/i18n"

  let { action, competition, embed } = $props()
  const language = getCurrentLanguage()

  let ThumbmarkJS: any

  const entries = getRequirement(action, "Entries") || 0
  const actionUrl = getRequirement(action, "Url")

  async function processAction(action, verificationData = null) {
    const entriesEarned = getRequirement(action, "Entries") || 0

    const fingerprint = ThumbmarkJS ? await ThumbmarkJS.getFingerprint() : null
    const fingerprintData = ThumbmarkJS
      ? await ThumbmarkJS.getFingerprintData()
      : null

    const { encrypted, iv } = await encryptJson(fingerprintData, PUBLIC_HASH)

    updateCompetitionEntry(
      $userStore.user,
      competition,
      action,
      verificationData,
      fingerprint,
      encrypted,
    )

    userStoreActions.setUser({
      ...$userStore.user,
      completedActionIds: [...$userStore.user.completedActionIds, action.id],
    })

    const yourEntriesNum = Number($statsStore.yourEntries)
    const totalEntriesNum = Number($statsStore.totalEntries)
    statsStoreActions.setStats({
      yourEntries: yourEntriesNum + entriesEarned,
      totalEntries: totalEntriesNum + entriesEarned,
    })

    // If action requires verification, add it to the list
    let verificationRequired = Boolean(action.actions.verification)
    if (verificationRequired) {
      userStoreActions.setOpenActionId(action.id)
      userStoreActions.setActionsNeedingVerification([
        ...$userStore.actionsNeedingVerificationIds,
        action.id,
      ])
    } else {
      // Close the action
      userStoreActions.setOpenActionId(null)
    }
  }

  let actionContainer: HTMLElement | null = $state(null)

  onMount(async () => {
    try {
      await import(
        "https://cdn.jsdelivr.net/npm/@thumbmarkjs/thumbmarkjs/dist/thumbmark.umd.js"
      )
      ThumbmarkJS = (window as any).ThumbmarkJS
    } catch (error) {
      console.error("Error loading ThumbmarkJS:", error)
    }

    const handleClickOutside = (event) => {
      // Ignore clicks from within action-content
      if ((event.target as Element).closest(".action-details")) {
        return
      }

      if (actionContainer && !actionContainer.contains(event.target as Node)) {
        userStoreActions.setOpenActionId(null)
      }
    }

    document.addEventListener("click", handleClickOutside)

    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  })

  let entryPluralized = $derived(
    t("widget_action_entries", {
      count: Number(entries),
    }),
  )

  console.log("action", action.actions.platform)
</script>

<div class="action-details" bind:this={actionContainer}>
  {#if action.actions.platform === "Instagram"}
    <Instagram {action} {competition} {processAction} />
  {:else if action.actions.platform === "X (Twitter)"}
    <X {action} {competition} {processAction} />
  {:else if action.actions.platform === "Facebook"}
    <Facebook {action} {competition} {processAction} />
  {:else if action.actions.platform === "TikTok"}
    <Tiktok {action} {competition} {processAction} />
  {:else if action.actions.platform === "Viral Share"}
    <Referral {action} {competition} {processAction} {embed} />
  {:else if action.actions.platform === "Pinterest"}
    <Pinterest {action} {competition} {processAction} />
  {:else if action.actions.platform === "Telegram"}
    <Telegram {action} {competition} {processAction} />
  {:else if action.actions.platform === "Custom"}
    <CustomAction {action} {competition} {processAction} />
  {:else if action.actions.platform === "Secret Code"}
    <SecretCode {action} {competition} {processAction} />
  {:else if action.actions.platform === "Google"}
    <Google {action} {competition} {processAction} />
  {:else if action.actions.platform === "Youtube"}
    <Youtube {action} {competition} {processAction} />
  {:else if action.actions.platform === "Reddit"}
    <Reddit {action} {competition} {processAction} />
  {:else if action.actions.platform === "Promo Amp"}
    <PromoAmp {action} {competition} {processAction} />
  {:else if action.actions.platform === "LinkedIn"}
    <LinkedIn {action} {competition} {processAction} />
  {:else if action.actions.platform === "Apple Music"}
    <AppleMusic {action} {competition} {processAction} />
  {:else if action.actions.platform === "Threads"}
    <Threads {action} {competition} {processAction} />
  {:else if action.actions.platform === "Spotify"}
    <Spotify {action} {competition} {processAction} />
  {:else if action.actions.platform === "Coupon"}
    <CouponWheel {action} {competition} {processAction} />
  {:else if action.actions.platform === "WhatsApp" || action.actions.platform === "Whatsapp"}
    <WhatsApp {action} {competition} {processAction} />
  {:else}
    <div class="bg-white rounded-lg p-4 shadow-lg">
      <div class="mb-4">
        <h4 class="text-sm font-medium mb-2">
          {t("widget_action_worth", {
            count: entries,
            entries: entryPluralized,
          })}
        </h4>
      </div>

      <Button
        variant="default"
        class="w-full bg-black text-white hover:bg-gray-800 flex items-center justify-center gap-2"
        onclick={() => {
          if (actionUrl) {
            window.open(actionUrl, "_blank")
          }
          processAction(action)
        }}
      >
        {(action.selectedAction || action.actions)?.translations?.[language]
          ?.name}
        <MoveRight />
      </Button>
    </div>
  {/if}
</div>
